import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  RefreshCw, Zap, Loader2, CheckCircle2, XCircle, Eye, EyeOff,
  Users, Bell, Smartphone, Mail, TrendingUp, Megaphone, ChevronDown, ChevronUp,
  MessageCircle, ChevronLeft, ChevronRight, Send, Pencil, Trash2, Image as ImageIcon,
  X, Sparkles, Plus, Clock, Settings2, Link as LinkIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import API from "../../api/axiosConfig";
import CampaignImageField from "../../components/CampaignImageField";

const SEGMENT_LABELS = {
  todos:     "Todos los usuarios",
  inactivos: "Inactivos",
  nuevos:    "Nuevos sin pedido",
};

const STATUS_LABELS = {
  borrador:             "Borrador",
  pendiente_aprobacion: "Pendiente de aprobación",
  pendiente_edicion:    "Esperando edición (Telegram)",
  aprobada:             "Aprobada",
  rechazada:            "Rechazada",
  enviada:              "Enviada",
  fallida:              "Fallida",
};

const STATUS_COLORS = {
  borrador:             "bg-gray-100 text-gray-600",
  pendiente_aprobacion: "bg-amber-100 text-amber-700",
  pendiente_edicion:    "bg-violet-100 text-violet-700",
  aprobada:             "bg-blue-100 text-blue-700",
  rechazada:            "bg-red-100 text-red-500",
  enviada:              "bg-green-100 text-green-700",
  fallida:              "bg-red-100 text-red-700",
};

const CHANNELS = [
  { id: "push",     label: "Push",     icon: Bell },
  { id: "inapp",    label: "In-app",   icon: Smartphone },
  { id: "email",    label: "Email",    icon: Mail },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];

const RECIPIENT_FILTERS = [
  { id: undefined, label: "Todos" },
  { id: true,      label: "Vieron" },
  { id: false,     label: "No vieron" },
];

const RECIPIENTS_PAGE_SIZE = 20;

function RecipientsPanel({ campaignId }) {
  const [filter, setFilter] = useState(undefined);
  const [page, setPage] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [filter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: RECIPIENTS_PAGE_SIZE, page };
    if (filter !== undefined) params.read = filter;
    API.get(`/marketing/campaigns/${campaignId}/recipients`, { params })
      .then((res) => {
        if (cancelled) return;
        setRecipients(res.data?.data?.data ?? []);
        setTotal(res.data?.data?.total ?? 0);
        setPages(res.data?.data?.pages ?? 1);
      })
      .catch(() => { if (!cancelled) toast.error("Error al cargar los destinatarios"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaignId, filter, page]);

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-100 w-fit">
          {RECIPIENT_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                filter === f.id ? "bg-supernova-100 text-supernova-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{total} destinatario{total !== 1 ? "s" : ""}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
      ) : recipients.length === 0 ? (
        <p className="text-center text-xs text-gray-400 py-6">Nadie en esta categoría</p>
      ) : (
        <div className="rounded-lg border border-gray-100 bg-white overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="px-3 py-2 text-left font-semibold text-gray-500">Usuario</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">Email</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">Estado</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-2 font-medium text-gray-700">{r.user?.name ?? "—"}</td>
                  <td className="px-3 py-2 text-gray-500">{r.user?.email ?? "—"}</td>
                  <td className="px-3 py-2">
                    {r.read ? (
                      <span className="flex items-center gap-1 text-green-600 font-semibold"><Eye size={11} /> Vio</span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400"><EyeOff size={11} /> No vio</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-400">
                    {new Date(r.read ? r.updatedAt : r.createdAt).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && pages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={13} /> Anterior
          </button>
          <span className="text-xs text-gray-400">Página {page} de {pages}</span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Siguiente <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function CampaignRow({ campaign, onChanged }) {
  const [channels, setChannels] = useState(campaign.channels);
  const [busy, setBusy] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendingChannel, setResendingChannel] = useState(null);
  const [editingContent, setEditingContent] = useState(false);
  const [draftTitle, setDraftTitle] = useState(campaign.title);
  const [draftBody, setDraftBody] = useState(campaign.body);
  const [draftImageUrl, setDraftImageUrl] = useState(campaign.imageUrl ?? "");
  const [draftLinkUrl, setDraftLinkUrl] = useState(campaign.linkUrl ?? "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const editable = ["pendiente_aprobacion", "pendiente_edicion", "borrador"].includes(campaign.status);
  const showStats = campaign.deliveredCount > 0;

  const toggleChannel = async (id) => {
    if (!editable || busy) return;
    const next = channels.includes(id) ? channels.filter((c) => c !== id) : [...channels, id];
    if (next.length === 0) return toast.warn("Elegí al menos un canal");
    setChannels(next);
    try {
      await API.put(`/marketing/campaigns/${campaign.id}/channels`, { channels: next });
    } catch (e) {
      setChannels(channels);
      toast.error(e.response?.data?.message ?? "No se pudo actualizar el canal");
    }
  };

  const handleApprove = async () => {
    setBusy(true);
    try {
      await API.post(`/marketing/campaigns/${campaign.id}/approve`);
      toast.success("Campaña aprobada y enviada");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al aprobar la campaña");
    } finally { setBusy(false); }
  };

  const handleReject = async () => {
    setBusy(true);
    try {
      await API.post(`/marketing/campaigns/${campaign.id}/reject`);
      toast.success("Campaña rechazada");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al rechazar la campaña");
    } finally { setBusy(false); }
  };

  const handleResend = async (channel) => {
    setResendingChannel(channel);
    try {
      await API.post(`/marketing/campaigns/${campaign.id}/resend`, { channel });
      toast.success(`Reenviado por ${CHANNELS.find((c) => c.id === channel)?.label ?? channel}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo reenviar");
    } finally { setResendingChannel(null); }
  };

  const handleSaveContent = async () => {
    if (!draftTitle.trim() || !draftBody.trim()) return toast.warn("Completá título y cuerpo");
    setSaving(true);
    try {
      await API.put(`/marketing/campaigns/${campaign.id}`, {
        title:    draftTitle.trim(),
        body:     draftBody.trim(),
        imageUrl: draftImageUrl.trim(),
        linkUrl:  draftLinkUrl.trim(),
      });
      toast.success("Campaña actualizada");
      setEditingContent(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const handleSendTest = async () => {
    if (!draftTitle.trim() || !draftBody.trim()) return toast.warn("Completá título y cuerpo");
    if (!testEmail.trim()) return toast.warn("Ingresá un correo para la prueba");
    setSendingTest(true);
    try {
      await API.post("/marketing/test-email", {
        title:    draftTitle.trim(),
        body:     draftBody.trim(),
        imageUrl: draftImageUrl.trim(),
        linkUrl:  draftLinkUrl.trim(),
        email:    testEmail.trim(),
      });
      toast.success(`Correo de prueba enviado a ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo enviar el correo de prueba");
    } finally { setSendingTest(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Eliminar esta campaña?")) return;
    setDeleting(true);
    try {
      await API.delete(`/marketing/campaigns/${campaign.id}`);
      toast.success("Campaña eliminada");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo eliminar");
      setDeleting(false);
    }
  };

  const openRatePct = campaign.openRate != null ? Math.round(campaign.openRate * 100) : null;

  return (
    <React.Fragment>
    <tr className="border-b border-gray-50 hover:bg-gray-50 transition align-top">
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
        {new Date(campaign.date).toLocaleDateString("es-CO")}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs font-semibold text-gray-700">{SEGMENT_LABELS[campaign.segment] ?? campaign.segment}</span>
        {campaign.audienceCount != null && (
          <p className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
            <Users size={10} /> {campaign.audienceCount} usuarios
          </p>
        )}
      </td>
      <td className="px-4 py-3 max-w-xs">
        <p className="font-semibold text-gray-800 text-sm flex items-center gap-1">
          {campaign.title}
          {campaign.imageUrl && <ImageIcon size={11} className="text-gray-300 shrink-0" />}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{campaign.body}</p>
        {editable && (
          <button
            onClick={() => { setDraftTitle(campaign.title); setDraftBody(campaign.body); setDraftImageUrl(campaign.imageUrl ?? ""); setDraftLinkUrl(campaign.linkUrl ?? ""); setEditingContent((v) => !v); }}
            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 mt-1"
          >
            <Pencil size={10} /> Editar
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {CHANNELS.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={!editable || busy}
              onClick={() => toggleChannel(c.id)}
              className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border transition ${
                channels.includes(c.id)
                  ? "bg-supernova-50 border-supernova-300 text-supernova-700"
                  : "bg-white border-gray-200 text-gray-400"
              } ${editable ? "cursor-pointer hover:border-supernova-400" : "cursor-default opacity-70"}`}
            >
              <c.icon size={10} /> {c.label}
            </button>
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[campaign.status]}`}>
          {STATUS_LABELS[campaign.status] ?? campaign.status}
        </span>
      </td>
      <td className="px-4 py-3">
        {showStats ? (
          <button
            type="button"
            onClick={() => setDetailOpen((v) => !v)}
            className="text-left hover:opacity-80 transition"
          >
            <span className="flex items-center gap-1 text-xs font-bold text-gray-800">
              <Eye size={12} className="text-gray-400" /> {campaign.viewedCount}/{campaign.deliveredCount}
              {detailOpen ? <ChevronUp size={11} className="text-gray-400" /> : <ChevronDown size={11} className="text-gray-400" />}
            </span>
            <div className="mt-1 h-1.5 w-20 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-supernova-400 rounded-full" style={{ width: `${openRatePct ?? 0}%` }} />
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5">{openRatePct ?? 0}% vio · ver detalle</p>
          </button>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {campaign.status === "pendiente_aprobacion" && (
            <>
              <button
                onClick={handleApprove}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 text-xs font-semibold rounded-lg transition disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Aprobar
              </button>
              <button
                onClick={handleReject}
                disabled={busy}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 text-xs font-semibold rounded-lg transition disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Rechazar
              </button>
            </>
          )}
          {campaign.status === "enviada" && (
            <button
              onClick={() => setResendOpen((v) => !v)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition"
            >
              <Send size={12} /> Reenviar
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
            title="Eliminar campaña"
          >
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
          </button>
        </div>
      </td>
    </tr>
    {editingContent && (
      <tr className="bg-gray-50/60">
        <td colSpan={7} className="px-4 py-3">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 block">Título</label>
            <input
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
            />
            <label className="text-xs font-semibold text-gray-500 block">Cuerpo</label>
            <textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300 resize-none"
            />
            <CampaignImageField value={draftImageUrl} onChange={setDraftImageUrl} label="Imagen del email (opcional)" />
            <div>
              <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1">
                <LinkIcon size={11} /> Enlace del botón "Abrir Qkiere" (opcional)
              </label>
              <input
                value={draftLinkUrl}
                onChange={(e) => setDraftLinkUrl(e.target.value)}
                placeholder="https://app.qkiere.com/..."
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1">
                <Mail size={11} /> Probar el correo antes de enviar (opcional)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tucorreo@ejemplo.com"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
                />
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={sendingTest}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition disabled:opacity-50 whitespace-nowrap"
                >
                  {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Enviar prueba
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveContent}
                disabled={saving}
                className="px-3 py-1.5 bg-supernova-500 hover:bg-supernova-600 text-black text-xs font-bold rounded-lg transition disabled:opacity-50"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
              <button
                onClick={() => setEditingContent(false)}
                className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-600 text-xs font-bold rounded-lg transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </td>
      </tr>
    )}
    {resendOpen && campaign.status === "enviada" && (
      <tr className="bg-gray-50/60">
        <td colSpan={7} className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500">Reenviar esta misma notificación por:</span>
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                onClick={() => handleResend(c.id)}
                disabled={resendingChannel !== null}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-supernova-400 text-gray-700 text-xs font-semibold rounded-lg transition disabled:opacity-50"
              >
                {resendingChannel === c.id ? <Loader2 size={12} className="animate-spin" /> : <c.icon size={12} />}
                {c.label}
              </button>
            ))}
          </div>
        </td>
      </tr>
    )}
    {detailOpen && showStats && (
      <tr className="bg-gray-50/60">
        <td colSpan={7} className="px-4 py-3">
          <RecipientsPanel campaignId={campaign.id} />
        </td>
      </tr>
    )}
    </React.Fragment>
  );
}

function NewCampaignForm({ onClose, onCreated }) {
  const [segment, setSegment] = useState("todos");
  const [channels, setChannels] = useState(["push", "inapp", "email"]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [audienceCount, setAudienceCount] = useState(null);
  const [assisting, setAssisting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const toggleChannel = (id) => {
    setChannels((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const handleAssist = async () => {
    setAssisting(true);
    try {
      const res = await API.post("/marketing/assist", { segment });
      const draft = res.data?.data;
      setTitle(draft?.title ?? "");
      setBody(draft?.body ?? "");
      setAudienceCount(draft?.audienceCount ?? null);
      toast.success("Borrador generado — revisalo y ajustalo antes de crear la campaña");
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo generar el borrador con IA");
    } finally { setAssisting(false); }
  };

  const handleSendTest = async () => {
    if (!title.trim() || !body.trim()) return toast.warn("Completá título y cuerpo (o generá con IA)");
    if (!testEmail.trim()) return toast.warn("Ingresá un correo para la prueba");
    setSendingTest(true);
    try {
      await API.post("/marketing/test-email", {
        title:    title.trim(),
        body:     body.trim(),
        imageUrl: imageUrl.trim(),
        linkUrl:  linkUrl.trim(),
        email:    testEmail.trim(),
      });
      toast.success(`Correo de prueba enviado a ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo enviar el correo de prueba");
    } finally { setSendingTest(false); }
  };

  const handleCreate = async () => {
    if (!title.trim() || !body.trim()) return toast.warn("Completá título y cuerpo (o generá con IA)");
    if (channels.length === 0) return toast.warn("Elegí al menos un canal");
    setCreating(true);
    try {
      await API.post("/marketing/campaigns", {
        segment,
        channels,
        title:    title.trim(),
        body:     body.trim(),
        imageUrl: imageUrl.trim(),
        linkUrl:  linkUrl.trim(),
      });
      toast.success("Campaña creada — quedó pendiente de aprobación");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo crear la campaña");
    } finally { setCreating(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black text-gray-800">Nueva campaña</h3>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 transition">
          <X size={16} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">Segmento</label>
          <select
            value={segment}
            onChange={(e) => { setSegment(e.target.value); setAudienceCount(null); }}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
          >
            {Object.entries(SEGMENT_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
          {audienceCount != null && (
            <p className="flex items-center gap-1 text-[11px] text-gray-400 mt-1">
              <Users size={10} /> {audienceCount} usuarios en este segmento
            </p>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">Canales</label>
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleChannel(c.id)}
                className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition ${
                  channels.includes(c.id)
                    ? "bg-supernova-50 border-supernova-300 text-supernova-700"
                    : "bg-white border-gray-200 text-gray-400 hover:border-supernova-400"
                }`}
              >
                <c.icon size={10} /> {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold text-gray-500">Título</label>
          <button
            type="button"
            onClick={handleAssist}
            disabled={assisting}
            className="flex items-center gap-1 text-[11px] font-semibold text-supernova-700 hover:text-supernova-800 disabled:opacity-50"
          >
            {assisting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            Generar con IA
          </button>
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ej: Hoy hay algo rico esperándote 🍔"
          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-500 block mb-1">Cuerpo</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Mensaje corto y cercano..."
          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300 resize-none"
        />
      </div>

      <CampaignImageField value={imageUrl} onChange={setImageUrl} label="Imagen del email (opcional)" />

      <div>
        <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1">
          <LinkIcon size={11} /> Enlace del botón "Abrir Qkiere" (opcional)
        </label>
        <input
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="https://app.qkiere.com/..."
          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1">
          <Mail size={11} /> Probar el correo antes de enviar (opcional)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="tucorreo@ejemplo.com"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-300"
          />
          <button
            type="button"
            onClick={handleSendTest}
            disabled={sendingTest}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition disabled:opacity-50 whitespace-nowrap"
          >
            {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Enviar prueba
          </button>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleCreate}
          disabled={creating}
          className="flex items-center gap-2 px-4 py-2 bg-supernova-500 hover:bg-supernova-600 text-black text-xs font-bold rounded-lg transition disabled:opacity-50"
        >
          {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          Crear campaña
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold rounded-lg transition"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function AutomationPanel({ onClose }) {
  const [schedule, setSchedule] = useState(null);
  const [marketingCron, setMarketingCron] = useState("");
  const [reportCron, setReportCron] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/marketing/cron");
      const data = res.data?.data;
      setSchedule(data);
      setMarketingCron(data?.marketingCronTime ?? "");
      setReportCron(data?.reportCronTime ?? "");
    } catch {
      toast.error("Error al cargar el horario de los cron");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!marketingCron.trim() || !reportCron.trim()) return toast.warn("Completá ambos horarios");
    setSaving(true);
    try {
      const res = await API.put("/marketing/cron", {
        marketingCronTime: marketingCron.trim(),
        reportCronTime:    reportCron.trim(),
      });
      setSchedule(res.data?.data);
      toast.success("Horario actualizado");
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo actualizar el horario (revisá la expresión cron)");
    } finally { setSaving(false); }
  };

  const handleSendSummary = async () => {
    setSendingSummary(true);
    try {
      await API.post("/marketing/summary");
      toast.success("Resumen enviado por Telegram");
    } catch (e) {
      toast.error(e.response?.data?.message ?? "No se pudo enviar el resumen (¿bot de Telegram configurado?)");
    } finally { setSendingSummary(false); }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }) : "—");

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black text-gray-800 flex items-center gap-1.5">
          <Settings2 size={15} /> Automatización (Telegram)
        </h3>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 transition">
          <X size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Parrilla diaria de marketing</label>
              <input
                value={marketingCron}
                onChange={(e) => setMarketingCron(e.target.value)}
                placeholder="0 8 * * *"
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-supernova-300"
              />
              <p className="flex items-center gap-1 text-[11px] text-gray-400 mt-1">
                <Clock size={10} /> Próxima ejecución: {fmt(schedule?.marketingNextRun)}
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Resumen diario de actividad</label>
              <input
                value={reportCron}
                onChange={(e) => setReportCron(e.target.value)}
                placeholder="0 20 * * *"
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-supernova-300"
              />
              <p className="flex items-center gap-1 text-[11px] text-gray-400 mt-1">
                <Clock size={10} /> Próxima ejecución: {fmt(schedule?.reportNextRun)}
              </p>
            </div>
          </div>
          <p className="text-[11px] text-gray-400">
            Formato cron estándar (minuto hora día mes día-semana) — zona horaria {schedule?.timezone ?? "America/Bogota"}.
          </p>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-supernova-500 hover:bg-supernova-600 text-black text-xs font-bold rounded-lg transition disabled:opacity-50"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              Guardar horario
            </button>
            <button
              onClick={handleSendSummary}
              disabled={sendingSummary}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition disabled:opacity-50"
            >
              {sendingSummary ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Enviar resumen ahora por Telegram
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function MarketingCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/marketing/campaigns", { params: { limit: 50 } });
      setCampaigns(res.data?.data?.data ?? []);
    } catch {
      toast.error("Error al cargar las campañas de marketing");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await API.post("/marketing/generate");
      const count = res.data?.data?.campaigns?.length ?? 0;
      toast.success(count > 0 ? `Parrilla generada: ${count} propuesta(s)` : "No se generaron propuestas nuevas (revisá ANTHROPIC_API_KEY o si ya no hay audiencia)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Error al generar la parrilla");
    } finally { setGenerating(false); }
  };

  const kpis = useMemo(() => {
    const sent = campaigns.filter((c) => c.status === "enviada");
    const delivered = sent.reduce((s, c) => s + (c.deliveredCount ?? 0), 0);
    const viewed = sent.reduce((s, c) => s + (c.viewedCount ?? 0), 0);
    return {
      sentCount: sent.length,
      delivered,
      viewed,
      rate: delivered > 0 ? Math.round((viewed / delivered) * 100) : null,
    };
  }, [campaigns]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-gray-800">Agente de Marketing</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Parrilla diaria generada por IA, aprobada por Telegram, con métricas reales de apertura.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-gray-500 hover:text-gray-700 transition">
            <RefreshCw size={17} />
          </button>
          <button
            onClick={() => setShowAutomation((v) => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-supernova-400 text-gray-700 text-sm font-bold rounded-xl transition"
          >
            <Settings2 size={15} />
            Automatización
          </button>
          <button
            onClick={() => setShowNewForm((v) => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-supernova-400 text-gray-700 text-sm font-bold rounded-xl transition"
          >
            <Plus size={15} />
            Nueva campaña
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-supernova-500 hover:bg-supernova-600 text-black text-sm font-bold rounded-xl transition disabled:opacity-50"
          >
            {generating ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
            Generar parrilla de hoy
          </button>
        </div>
      </div>

      {showAutomation && <AutomationPanel onClose={() => setShowAutomation(false)} />}

      {showNewForm && (
        <NewCampaignForm
          onClose={() => setShowNewForm(false)}
          onCreated={() => { setShowNewForm(false); load(); }}
        />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-3xl font-black text-gray-800">{kpis.sentCount}</p>
          <p className="text-sm font-medium text-gray-500 mt-0.5 flex items-center gap-1"><Megaphone size={13} /> Campañas enviadas</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-3xl font-black text-gray-800">{kpis.delivered}</p>
          <p className="text-sm font-medium text-gray-500 mt-0.5 flex items-center gap-1"><Bell size={13} /> Notificaciones entregadas</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-3xl font-black text-gray-800">{kpis.viewed}</p>
          <p className="text-sm font-medium text-gray-500 mt-0.5 flex items-center gap-1"><Eye size={13} /> Vistas reales</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <p className="text-3xl font-black text-gray-800">{kpis.rate != null ? `${kpis.rate}%` : "—"}</p>
          <p className="text-sm font-medium text-gray-500 mt-0.5 flex items-center gap-1"><TrendingUp size={13} /> Tasa de apertura</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-gray-300" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Megaphone size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">Sin campañas todavía</p>
            <p className="text-xs mt-1">Tocá "Generar parrilla de hoy" o esperá al cron diario</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Fecha</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Segmento</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Mensaje</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Canales</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Vistas</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <CampaignRow key={c.id} campaign={c} onChanged={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
